// import App from './App.vue';
import { createHead } from '@vueuse/head';
import TheApp from 'components/TheApp.vue';
import installUiKit from 'components/UiKit';
import config, { DEBUG, client, getEnv } from 'config';
import { each } from 'lodash';
import i18n, { store as i18nStore } from 'plugins/i18n';
import { App, createApp, nextTick } from 'vue';
// import installKoddeinVueForm from 'components/UiKit/KoddeinVueForm'
import useRecaptcha from 'plugins/recaptcha';
import useRouter, { observeFirstLocale } from 'plugins/router';
import AppStore from 'plugins/store/app';
import { store as globalsStore } from 'plugins/store/globals';
import { pinia } from 'plugins/store/index';
import InterfaceStore from 'plugins/store/interface';
import { store as previewStore } from 'plugins/store/preview';
import { store as trackingStore } from 'plugins/store/tracking';
import { Router } from 'vue-router';

// import vToolTip from 'vendors/v-tooltip';

declare global {
  interface Window {
    Userback: any
    Cookiebot: any
    // __ENV:O<Obj> // @see config/env
    // __GLOBALS:O<Obj> // @see plugins/store/globals
    __CACHE: O<Obj>
    __PAGE: O<Obj>
    __NEWS: O<Obj>
    __SEARCH: O<Obj>
    __TIMESTAMP: number
    __ROUTES?: any
    prerenderReady: boolean
    isPrerender: boolean
  }
}
/** main setup sequence
 * async because if devmode must async load route before
 * - kinda hack - no effet on prod since no file to load
 */
async function setup() {
  const router = useRouter();
  const head = createHead()

  const app = createApp(TheApp)
    .use(i18n)
    .use(pinia)
    .use(router)
    .use(head)
  // .use(vToolTip)

  import('vendors/v-tooltip')
    .then(m => m.default)
    .then(vToolTip => app.use(vToolTip))

  import('vendors/swiperjs')

  // Handle Craft CMS preview
  previewStore.init();

  // Init recaptcha if needed
  useRecaptcha(app);

  // Init userback qa scripts
  if (client && getEnv('VITE_USERBACK_FRONTEND_IS_ACTIVATE') && getEnv('VITE_USERBACK_ACCESS_TOKEN')) {
    window.Userback = window.Userback || {};
    window.Userback.access_token = getEnv('VITE_USERBACK_ACCESS_TOKEN');
    const userback = document.createElement('script');
    userback.async = true;
    userback.src = 'https://static.userback.io/widget/v1.js';
    (document.head || document.body).appendChild(userback);
  }

  // Init Tracking - pass router as params
  const plugins = trackingStore.INSTALL(router);
  if (plugins.length) {
    each(plugins, (plugin) => {
      app.use(plugin.install, plugin.opts);
    });
  }

  // install our global UI Kit components
  installUiKit(app)
  // can decide to install all the complete form system globally
  // installKoddeinVueForm(app)

  // wait locale before 1st render app so i18n pseudo ready etc
  observeFirstLocale(() => {
    // init interface - modernzr etc.
    InterfaceStore.init()
    // SET LOCALE HERE - then mount
    i18nStore.setLocaleByName("fr")
    // load globals api
    globalsStore.LOAD("fr")

    nextTick(() => {
      AppStore.setAppReady()
    })
    app.mount('#app')
  })

  silentSomeWarns(app)
  if (DEBUG) addDebugInConsole({ app, router })
}

export default setup()



// createApp(App).mount('#app')

/**
 * Add extra stuff on `window` global for easy console debug
 */
type addDebugInConsolePayload = {app:App, router:Router}
async function addDebugInConsole(tools:addDebugInConsolePayload){
  const debugTools = { ...tools, config, i18n, getEnv, store:pinia }
  each(debugTools, (value, key)=>{
    /* @ts-expect-error */
    window[`$${key}`] = value;
  })
  window[`_`] =  await import(/* webpackChunkName: "debug-lodash" */'lodash').then(m=>m.default);
  /* @ts-expect-error */
  window['USE_PWA'] = import.meta.env.USE_PWA

  document.documentElement.dataset.debug = 'true'
}


// Unregister service workers if no pwa mode
if (!import.meta.env.USE_PWA) {
  navigator?.serviceWorker?.getRegistrations()
    .then(registrations => {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
}

function silentSomeWarns(app: App) {
  /** may silent some warnings */
  app.config.warnHandler = (msg, instance, trace) => {
    console.debug('[warn] ', { msg, instance, trace });
    const msgs = [
      'built-in or reserved HTML elements as component id: component',
      '"class" is a reserved attribute and cannot be used as component prop',
      'Cannot find element: #__nuxt']

    if (msgs.some((warning) => msg.includes(warning))) return
    console.warn('[Vue warn]: '.concat(msg).concat(trace))
  }

}
